import { useIsPostsCommentsReactionsEnabled } from "@circle-react/hooks/useIsPostsCommentsReactionsEnabled";
import { useState } from "react";

import { t } from "@/i18n-js/instance";
import { useCommentLikes } from "@/react/components/CommentsCompass/data/useCommentLikes";
import {
  ModalManager,
  useModal,
} from "@/react/components/Layout/ModalManagerProvider";
import { resolveAllReactionsCount } from "@/react/components/Reactions/PostCommentReactionAction/helpers";
import { ReactionLikesDialog } from "@/react/components/Reactions/PostCommentReactionAction/ReactionLikesDialog";
import { toLocaleString } from "@/react/helpers/number";
import type { Comment } from "@/react/types/Comment";
import type { ContentReactionType } from "@/react/types/ContentReaction";
import { EMPTY_REACTION_COUNTS } from "@/react/types/ContentReaction";

import { MemberRow } from "./MemberRow";

interface LikesModalProps {
  comment: Comment;
}

const LikesModal = ModalManager.create(({ comment }: LikesModalProps) => {
  const modal = useModal();
  const isReactionsEnabled = useIsPostsCommentsReactionsEnabled();
  const [selectedReaction, setSelectedReaction] =
    useState<ContentReactionType | null>(null);
  const [allReactionsCount, setAllReactionsCount] = useState(
    comment.likes_count,
  );
  const { members, totalCount, isInitialLoading, hasNextPage, fetchNextPage } =
    useCommentLikes({ comment, reactionType: selectedReaction });

  const likesCount = totalCount ?? comment.likes_count;
  const title = isReactionsEnabled
    ? t("reactions.title")
    : t("comments.likes", {
        count: likesCount,
        formatted_count: toLocaleString(likesCount),
      });
  const displayedAllCount = resolveAllReactionsCount({
    allReactionsCount,
    selectedReaction,
    totalCount,
  });

  const handleReactionFilterChange = (
    reactionType: ContentReactionType | null,
  ) => {
    setAllReactionsCount(displayedAllCount);
    setSelectedReaction(reactionType);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) void modal.hide();
  };
  const handleOpenChangeComplete = (open: boolean) => {
    if (!open) modal.remove();
  };

  return (
    <ReactionLikesDialog
      open={modal.visible}
      onOpenChange={handleOpenChange}
      onOpenChangeComplete={handleOpenChangeComplete}
      title={title}
      members={members}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      isInitialLoading={isInitialLoading}
      skeletonCount={likesCount}
      reactionFilter={
        isReactionsEnabled
          ? {
              counts: comment.reaction_summary?.counts ?? EMPTY_REACTION_COUNTS,
              selectedReaction,
              totalCount: displayedAllCount,
              onChange: handleReactionFilterChange,
            }
          : undefined
      }
      renderMember={(communityMember, className) => (
        <MemberRow
          className={className}
          communityMember={communityMember}
          showReactionBadge={isReactionsEnabled}
          onNavigate={() => void modal.hide()}
        />
      )}
    />
  );
});

export const useLikesModal = () => useModal(LikesModal);
